// Third Party
import * as React from 'react';
import * as Sentry from '@sentry/nextjs';

// Utils
import { uploadFile as _uploadFile } from '@utils/upload-file';
import getConfig from '@config';
import { AxiosProgressEvent } from 'axios';

export type UploadProgress = { state: 'pending' | 'done'; percentage: number };
type UseFileUploader = {
  uploadFile: (file: File) => Promise<string | undefined>;
  uploading: boolean;
  uploadSuccessful: boolean;
  uploadProgress: UploadProgress;
};

export function useFileUploader(): UseFileUploader {
  const {
    publicRuntimeConfig: {
      aws: { uploadBucket },
    },
  } = getConfig();

  const [uploading, setUploading] = React.useState(false);
  const [uploadSuccessful, setUploadSuccessful] = React.useState(false);
  const [uploadProgress, setUploadProgress] = React.useState<UploadProgress>({
    state: 'pending',
    percentage: 0,
  });

  const handleProgress = (progressEvent: AxiosProgressEvent) => {
    const percentCompleted =
      progressEvent.total !== undefined
        ? Math.floor((progressEvent.loaded * 100) / progressEvent.total)
        : 0;
    setUploadProgress({ percentage: percentCompleted, state: 'pending' });
  };

  const uploadFile = async (file: File) => {
    setUploadProgress({ state: 'pending', percentage: 0 });
    setUploading(true);

    try {
      const response = await _uploadFile(uploadBucket, file, handleProgress);
      setUploadProgress({
        state: 'done',
        percentage: 100,
      });
      setUploadSuccessful(true);
      setUploading(false);

      return response;
    } catch (e) {
      Sentry.captureException(e);
      return undefined;
    }
  };

  return {
    uploadFile,
    uploading,
    uploadProgress,
    uploadSuccessful,
  };
}
